import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
interface CalendarProps {
  selectedDates: Date[];
  onDateSelect: (dates: Date[]) => void;
  blockedDates?: Date[];
  mode?: 'single' | 'range' | 'multi';
}
export function Calendar({
  selectedDates,
  onDateSelect,
  blockedDates = [],
  mode = 'range'
}: CalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const isSameDay = (a: Date, b: Date) => a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
  const isDateBlocked = (date: Date) => {
    return blockedDates.some(blocked => isSameDay(blocked, date));
  };
  const isDateSelected = (date: Date) => {
    return selectedDates.some(selected => isSameDay(selected, date));
  };
  const isPastDate = (date: Date) => {
    return date < today;
  };
  const isDateInRange = (date: Date) => {
    if (mode !== 'range' || selectedDates.length !== 2) return false;
    const [start, end] = selectedDates.sort((a, b) => a.getTime() - b.getTime());
    return date > start && date < end;
  };
  const handleDateClick = (day: number) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    if (isDateBlocked(date) || isPastDate(date)) return;
    if (mode === 'multi') {
      // Toggle: if already selected, remove it; otherwise add it
      if (isDateSelected(date)) {
        onDateSelect(selectedDates.filter(d => !isSameDay(d, date)));
      } else {
        onDateSelect([...selectedDates, date]);
      }
    } else if (mode === 'single') {
      onDateSelect([date]);
    } else {
      if (selectedDates.length === 0 || selectedDates.length === 2) {
        onDateSelect([date]);
      } else {
        onDateSelect([...selectedDates, date].sort((a, b) => a.getTime() - b.getTime()));
      }
    }
  };
  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };
  // Count selected dates in current month view
  const selectedInMonth = selectedDates.filter(d => d.getMonth() === currentMonth.getMonth() && d.getFullYear() === currentMonth.getFullYear()).length;
  return <div className="bg-white rounded-ios-lg p-4" data-id="element-152">
      {/* Header */}
      <div className="flex items-center justify-between mb-4" data-id="element-153">
        <button onClick={previousMonth} className="p-2 hover:bg-sand rounded-full transition-colors" data-id="element-154">
          <ChevronLeft size={20} className="text-charcoal" data-id="element-155" />
        </button>
        <span className="font-semibold text-charcoal" data-id="element-156">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </span>
        <button onClick={nextMonth} className="p-2 hover:bg-sand rounded-full transition-colors" data-id="element-157">
          <ChevronRight size={20} className="text-charcoal" data-id="element-158" />
        </button>
      </div>

      {/* Day Labels */}
      <div className="grid grid-cols-7 gap-2 mb-2" data-id="element-159">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => <div key={day} className="text-center text-xs font-medium text-moss-gray" data-id="element-160">
            {day}
          </div>)}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-2" data-id="element-161">
        {Array.from({
        length: firstDayOfMonth
      }).map((_, index) => <div key={`empty-${index}`} data-id="element-162" />)}
        {Array.from({
        length: daysInMonth
      }).map((_, index) => {
        const day = index + 1;
        const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
        const blocked = isDateBlocked(date);
        const selected = isDateSelected(date);
        const inRange = isDateInRange(date);
        const past = isPastDate(date);
        const isDisabled = blocked || past;
        return <motion.button key={day} onClick={() => handleDateClick(day)} disabled={isDisabled} whileTap={isDisabled ? {} : {
          scale: 0.9
        }} className={`
                aspect-square rounded-full flex items-center justify-center text-sm font-medium
                transition-colors relative
                ${past ? 'text-moss-gray/25 cursor-not-allowed' : ''}
                ${blocked && !past ? 'text-moss-gray/30 cursor-not-allowed line-through' : ''}
                ${selected && mode === 'multi' ? 'bg-red-500/15 text-red-600 line-through font-semibold' : ''}
                ${selected && mode !== 'multi' ? 'bg-dark-green text-white' : ''}
                ${inRange ? 'bg-light-green/20 text-dark-green' : ''}
                ${!isDisabled && !selected && !inRange ? 'text-charcoal hover:bg-sand' : ''}
              `} data-id="element-163">
              {day}
            </motion.button>;
      })}
      </div>

      {/* Selected count for multi mode */}
      {mode === 'multi' && selectedDates.length > 0 && <div className="mt-3 pt-3 border-t border-sand/50 flex items-center justify-between" data-id="element-164">
          <span className="text-xs text-charcoal/60" data-id="element-165">
            {selectedDates.length} date{selectedDates.length !== 1 ? 's' : ''}{' '}
            marked unavailable
          </span>
          <button onClick={() => onDateSelect([])} className="text-xs font-medium text-dark-green hover:underline" data-id="element-166">
            Clear all
          </button>
        </div>}
    </div>;
}